import React from 'react';
import {
  APP_NAME,
  BASE_URL,
  ORG_LOGO_HEIGHT,
  ORG_LOGO_PATH,
  ORG_LOGO_WIDTH,
} from '@apps/config';
import { dateConversions, dateIsoFormat } from '@apps/utils/date-conversion';
import { serializeJsonLd } from '@apps/utils/serialize-json-ld';
import { stripHtml } from '@apps/utils/strip-html';
import Head from 'next/head';

import { NewsArticleStructuredDataProps } from './props';

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const NewsArticleStructuredData: React.FC<
  NewsArticleStructuredDataProps
> = (props) => {
  const { data, url, locale } = props;

  // =============== VARIABLES
  if (!data) return null;

  const headline = data.post_title?.[locale];
  // fall back to the article body when the post has no short description
  const description =
    data.post_short_description?.[locale] ||
    stripHtml(data.post_description?.[locale]).slice(0, 160);
  const image = data.post_image_full?.[locale] || data.post_image?.[locale];

  /**
   * `post_created` arrives as `DD-MM-YYYY hh:mm A`, so it goes through the same
   * two step conversion already used by `containers/NewsDetailSection`
   */
  const fullDate = dateConversions(data.post_created, true) as string;
  const publishedAt = fullDate
    ? (dateIsoFormat(fullDate) as string)
    : undefined;

  // author profile pages are keyed by the english username
  const authorUsername = data.post_author_username?.['en']?.toLowerCase();

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline,
    description,
    image,
    datePublished: publishedAt,
    // the post API exposes no modified timestamp, so it mirrors `datePublished`
    dateModified: publishedAt,
    author: {
      '@type': 'Person',
      name: data.post_author?.[locale],
      ...(authorUsername
        ? { url: `${BASE_URL}/author/${authorUsername}` }
        : {}),
    },
    publisher: {
      '@type': 'Organization',
      name: APP_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}${ORG_LOGO_PATH}`,
        width: ORG_LOGO_WIDTH,
        height: ORG_LOGO_HEIGHT,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };

  // =============== VIEWS
  return (
    <Head>
      <script
        type="application/ld+json"
        key="news-article-schema"
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(articleSchema) }}
      />
    </Head>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export * from './props';
export default NewsArticleStructuredData;
